import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import type {
  CustomersReport,
  LicensesReport,
  ProjectsReport,
  SalesReport,
  SubscriptionsReport,
} from "../lib/types";
import {
  Card,
  EmptyState,
  Figure,
  PageHeader,
  Table,
  TableWrap,
  Td,
  Th,
  formatMoney,
  statusLabel,
} from "../components/ui";

/**
 * Where an admin lands, not another instrument. Reports answers "what
 * happened in this range, filtered to this product"; this answers "how does
 * the business stand right now" — no filters, because the question it answers
 * has no period to pick.
 *
 * Every card is the same report Reports.tsx already shows, called with no
 * filter, so the two screens can never disagree about a number — there is
 * only one query for each of them.
 */
export function Dashboard() {
  const sales = useQuery({
    queryKey: ["report", "sales", ""],
    queryFn: () => api<SalesReport>("/api/v1/admin/reports/sales"),
  });
  const customers = useQuery({
    queryKey: ["report", "customers", ""],
    queryFn: () => api<CustomersReport>("/api/v1/admin/reports/customers"),
  });
  const projects = useQuery({
    queryKey: ["report", "projects", ""],
    queryFn: () => api<ProjectsReport>("/api/v1/admin/reports/projects"),
  });
  const subscriptions = useQuery({
    queryKey: ["report", "subscriptions", ""],
    queryFn: () => api<SubscriptionsReport>("/api/v1/admin/reports/subscriptions"),
  });
  const licenses = useQuery({
    queryKey: ["report", "licenses", ""],
    queryFn: () => api<LicensesReport>("/api/v1/admin/reports/licenses"),
  });

  const attention: Array<{ label: string; count: number; to: string }> = [
    {
      label: `Subscriptions expiring within ${subscriptions.data?.expiringWithinDays ?? 30} days`,
      count: subscriptions.data?.expiring ?? 0,
      to: "/reports",
    },
    {
      label: `Projects expiring within ${projects.data?.expiringWithinDays ?? 30} days`,
      count: projects.data?.expiring ?? 0,
      to: "/projects",
    },
    {
      label: "Projects with no paid order behind them",
      count: projects.data?.withoutPaidOrder ?? 0,
      to: "/projects",
    },
    {
      label: "Customers with no way to sign in",
      count: customers.data?.withoutPortalAccess ?? 0,
      to: "/customers",
    },
  ].filter((row) => row.count > 0);

  const loading =
    sales.isPending ||
    customers.isPending ||
    projects.isPending ||
    subscriptions.isPending ||
    licenses.isPending;

  return (
    <div className="space-y-6">
      <PageHeader title="Dashboard" lede="The state of the business, right now." />

      <Card
        title="Revenue"
        action={<span className="text-xs text-dim">All paid orders, all time</span>}
      >
        {sales.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : sales.data === undefined || sales.data.totals.length === 0 ? (
          <EmptyState>Nothing has been paid for yet.</EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Currency</Th>
                  <Th>Orders</Th>
                  <Th>Revenue</Th>
                  <Th>Average order</Th>
                </tr>
              </thead>
              <tbody>
                {sales.data.totals.map((total) => (
                  <tr key={total.currency} className="border-b border-hairline last:border-0">
                    <Td label="Currency" className="tabular">
                      {total.currency}
                    </Td>
                    <Td label="Orders" className="tabular">
                      {total.orders}
                    </Td>
                    <Td label="Revenue" className="tabular text-ink">
                      {formatMoney(total.revenueCents, total.currency)}
                    </Td>
                    <Td label="Average order" className="tabular text-muted">
                      {formatMoney(total.averageOrderValueCents, total.currency)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {/* Nothing to act on is a fine state and says so plainly, rather than an
          empty card that reads as broken. */}
      <Card title="Needs attention">
        {loading ? (
          <EmptyState>Loading…</EmptyState>
        ) : attention.length === 0 ? (
          <EmptyState>Nothing is waiting on you.</EmptyState>
        ) : (
          <ul className="divide-y divide-hairline">
            {attention.map((row) => (
              <li key={row.label} className="flex items-center justify-between gap-4 px-4 py-3">
                <span className="text-sm text-ink">{row.label}</span>
                <Link
                  to={row.to}
                  className="shrink-0 rounded-md bg-raised px-2.5 py-1 text-sm tabular text-ink transition-colors hover:border hover:border-accent/60"
                >
                  {row.count}
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card title="Customers">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            <Figure label="On the books" value={customers.data?.total} />
            <Figure label="Active" hint="Something of theirs is live" value={customers.data?.active} />
            <Figure
              label="No portal login"
              hint="Normal, not broken"
              value={customers.data?.withoutPortalAccess}
            />
          </dl>
        </Card>

        <Card title="Licences">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            {(licenses.data?.byStatus ?? []).map((row) => (
              <Figure key={row.status} label={statusLabel(row.status)} value={row.count} />
            ))}
            <Figure
              label="Seats in use"
              hint="Activations not revoked"
              value={licenses.data?.activeActivations}
            />
          </dl>
        </Card>

        <Card title="Projects">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            {(projects.data?.byStatus ?? []).map((row) => (
              <Figure key={row.status} label={statusLabel(row.status)} value={row.count} />
            ))}
          </dl>
        </Card>

        <Card title="Subscriptions">
          <dl className="grid grid-cols-2 gap-px bg-hairline sm:grid-cols-3">
            {(subscriptions.data?.byStatus ?? []).map((row) => (
              <Figure key={row.status} label={statusLabel(row.status)} value={row.count} />
            ))}
          </dl>
        </Card>
      </div>

      <p className="text-xs text-dim">
        Want a period, a product, or a marketer instead of everything to date? That is{" "}
        <Link to="/reports" className="text-accent hover:underline">
          Reports
        </Link>
        .
      </p>
    </div>
  );
}
